import { z } from 'zod';
import { PARAMETER_FORMULA_KEYS } from '../parameter-formulas';

// These mirror the enums in prisma/schema.prisma. They are declared here rather
// than imported from @prisma/client so the browser bundle never pulls in Prisma.
// server/prisma/enum-parity.ts asserts at compile time that the two stay in sync.

export const genderSchema = z.enum(['male', 'female', 'other']);
export type Gender = z.infer<typeof genderSchema>;

/** A per-user exception to their role. See `shared/permissions.ts`. */
export const permissionEffectSchema = z.enum(['allow', 'deny']);

export const maritalStatusSchema = z.enum(['single', 'married']);
export type MaritalStatus = z.infer<typeof maritalStatusSchema>;

export type PaymentStatus = 'paid' | 'partial' | 'pending';

/**
 * How the money arrived.
 *
 * A recorded fact, never an input to the bill — `shared/pricing.ts` does not
 * import this and must not: that file deliberately imports nothing so the demo
 * seed can load it under Node's type stripping.
 */
export const paymentMethodSchema = z.enum([
  'cash',
  'card',
  'bank_transfer',
  'mobile_wallet',
  'cheque',
  'panel',
]);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

/**
 * Why a login session stopped counting. Only `submitted` produced a Day Close;
 * every other ending is a missing submission on the reconciliation screen.
 */
export type SessionEndReason = 'signed_out' | 'submitted' | 'superseded' | 'expired';

export const reportStatusSchema = z.enum(['registered', 'in_progress', 'completed']);
export type ReportStatus = z.infer<typeof reportStatusSchema>;

/** Where one WhatsApp send has got to. See server/whatsapp/share-ladder.ts. */
export type WhatsappShareStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed';

/** Whether somebody pressed Share, or the visit's last release sent it. */
export type WhatsappShareTrigger = 'manual' | 'auto';

/**
 * Where a stored result sat relative to its reference range.
 *
 * Not the same set as `OutOfRangeFlag` in shared/reference-range.ts, which is
 * what the badge on screen uses. That one is `'high' | 'low' | null`, where the
 * null covers three unrelated situations. This one names the in-range case, and
 * the *column* is nullable for the rest — so an abnormal rate can tell "inside
 * the range" apart from "no range to be inside of".
 */
export type ResultFlag = 'normal' | 'high' | 'low';

/** How a test's results are recorded and printed. */
export const reportKindSchema = z.enum(['parameters', 'culture']);
export type ReportKind = z.infer<typeof reportKindSchema>;

/** Whether a culture grew anything. */
export const cultureOutcomeSchema = z.enum(['growth', 'no_growth']);
export type CultureOutcome = z.infer<typeof cultureOutcomeSchema>;

/** How heavily an isolated organism grew. */
export const growthGradeSchema = z.enum(['scanty', 'light', 'moderate', 'heavy']);
export type GrowthGrade = z.infer<typeof growthGradeSchema>;

/** One antibiotic's interpretation. Printed as the short codes in `SUSCEPTIBILITY_CODES`. */
export const susceptibilitySchema = z.enum(['sensitive', 'intermediate', 'resistant']);
export type Susceptibility = z.infer<typeof susceptibilitySchema>;

/** How a sensitivity prints, matching the CLSI legend on the report. */
export const SUSCEPTIBILITY_CODES = {
  sensitive: 'S',
  intermediate: 'IS',
  resistant: 'R',
} as const satisfies Record<Susceptibility, string>;

/** A calculated parameter's formula. Labels and arithmetic: `PARAMETER_FORMULAS`. */
export const parameterFormulaSchema = z.enum(PARAMETER_FORMULA_KEYS);

/**
 * How a reagent item is counted.
 *
 * A display label only. No arithmetic in the inventory reads it — every
 * quantity is in the item's own base unit and `pack_size_base` is the single
 * conversion. See the note on `ReagentItem` in the schema.
 */
export const stockUnitSchema = z.enum(['ul', 'ml', 'l', 'mg', 'g', 'test', 'piece', 'vial']);
export type StockUnit = z.infer<typeof stockUnitSchema>;

export const stockMovementKindSchema = z.enum([
  'receipt',
  'consumption',
  'wastage',
  'adjustment',
  'opening_balance',
  'return_to_supplier',
]);
export type StockMovementKind = z.infer<typeof stockMovementKindSchema>;

export const lotStatusSchema = z.enum(['active', 'quarantined', 'discarded']);
export type LotStatus = z.infer<typeof lotStatusSchema>;

/** What a spreadsheet is being imported *into*. */
export const importEntitySchema = z.enum([
  'lab_tests',
  'reagent_items',
  'patients',
  'report_results',
]);
export type ImportEntity = z.infer<typeof importEntitySchema>;

/**
 * Where an import has got to.
 *
 * `partially_committed` is a first-class state, not a failure: commit runs in
 * 300-row chunks with a per-row retry, so a handful of bad rows leave the other
 * thousands legitimately written. Collapsing that into `failed` would invite
 * someone to re-run the whole file and double every row that already landed.
 */
export type ImportRunStatus =
  | 'staged'
  | 'validating'
  | 'validated'
  | 'committing'
  | 'committed'
  | 'partially_committed'
  | 'failed';

/**
 * Where one staged row has got to.
 *
 * `skipped` is deliberately distinct from `error`. The trailing rows of the
 * lab's Patient History sheet have only the year filled in; a hundred red rows
 * at the bottom of a preview teaches the operator to ignore red, so they are
 * counted and shown in their own colour instead.
 */
export const importRowStatusSchema = z.enum([
  'pending',
  'ok',
  'warning',
  'error',
  'skipped',
  'committed',
  'failed',
]);
export type ImportRowStatus = z.infer<typeof importRowStatusSchema>;

/**
 * What the commit is allowed to do to rows that already exist.
 *
 * Orthogonal to `preserve_existing`, which decides what an *absent* cell means.
 */
export const importWriteModeSchema = z.enum(['create_only', 'create_and_update', 'update_only']);
export type ImportWriteMode = z.infer<typeof importWriteModeSchema>;

/** Where a reagent order has got to. See the ladder in `requisition-ladder.ts`. */
export const requisitionStatusSchema = z.enum([
  'draft',
  'submitted',
  'approved',
  'rejected',
  'ordered',
  'partially_received',
  'received',
  'cancelled',
]);
export type RequisitionStatus = z.infer<typeof requisitionStatusSchema>;

/**
 * Why money left the lab.
 *
 * `cogs` is the one the app derives for itself — reagent consumption is already
 * in the stock ledger at cost. The rest are typed in.
 */
export const expenseKindSchema = z.enum([
  'cogs',
  'payroll',
  'overhead',
  'marketing',
  'equipment',
  'other',
]);
export type ExpenseKind = z.infer<typeof expenseKindSchema>;

/** Where a request for tests has got to. */
export const orderRequestStatusSchema = z.enum(['pending', 'registered', 'declined']);
export type OrderRequestStatus = z.infer<typeof orderRequestStatusSchema>;

/** Human-readable labels, used to build <Select> options on the client. */
export const ENUM_LABELS = {
  gender: { male: 'Male', female: 'Female', other: 'Other' },
  maritalStatus: { single: 'Single', married: 'Married' },
  paymentStatus: { paid: 'Paid', partial: 'Partial', pending: 'Pending' },
  paymentMethod: {
    cash: 'Cash',
    card: 'Card',
    bank_transfer: 'Bank transfer',
    mobile_wallet: 'Mobile wallet',
    cheque: 'Cheque',
    panel: 'Panel account',
  },
  sessionEndReason: {
    signed_out: 'Signed out',
    submitted: 'Submitted',
    superseded: 'Signed in elsewhere',
    expired: 'Expired',
  },
  reportStatus: { registered: 'Registered', in_progress: 'In progress', completed: 'Completed' },
  // No label for the null case on purpose: it is not a fourth value of this
  // enum, it is the absence of one. Screens that need to name it say "Not
  // judgeable" themselves, next to the count it applies to.
  resultFlag: { normal: 'Normal', high: 'High', low: 'Low' },
  reportKind: { parameters: 'Parameter results', culture: 'Culture & sensitivity' },
  cultureOutcome: { growth: 'Growth', no_growth: 'No growth' },
  growthGrade: { scanty: 'Scanty', light: 'Light', moderate: 'Moderate', heavy: 'Heavy' },
  susceptibility: {
    sensitive: 'Sensitive',
    intermediate: 'Intermediate sensitive',
    resistant: 'Resistant',
  },
  stockUnit: {
    // µ, not "u": this is what prints on a screen and in a CSV export, and the
    // SOP documents the recipes come from are written in µL.
    ul: 'µL',
    ml: 'mL',
    l: 'L',
    mg: 'mg',
    g: 'g',
    test: 'tests',
    piece: 'pieces',
    vial: 'vials',
  },
  stockMovementKind: {
    receipt: 'Received',
    consumption: 'Consumed',
    wastage: 'Wastage',
    adjustment: 'Adjustment',
    opening_balance: 'Opening balance',
    return_to_supplier: 'Returned to supplier',
  },
  lotStatus: { active: 'Active', quarantined: 'Quarantined', discarded: 'Discarded' },
  importEntity: {
    lab_tests: 'Test catalogue and rate list',
    reagent_items: 'Reagents and opening stock',
    patients: 'Patient visit history',
    report_results: 'Past results',
  },
  importRunStatus: {
    staged: 'Staged',
    validating: 'Checking',
    validated: 'Checked',
    committing: 'Importing',
    committed: 'Imported',
    partially_committed: 'Partly imported',
    failed: 'Failed',
  },
  importRowStatus: {
    pending: 'Not checked',
    ok: 'Ready',
    warning: 'Ready, with a warning',
    error: 'Needs attention',
    skipped: 'Skipped',
    committed: 'Imported',
    failed: 'Failed',
  },
  importWriteMode: {
    create_only: 'Only add new rows',
    create_and_update: 'Add new rows and update existing ones',
    update_only: 'Only update existing rows',
  },
  requisitionStatus: {
    draft: 'Draft',
    submitted: 'Waiting for approval',
    approved: 'Approved',
    rejected: 'Rejected',
    ordered: 'Ordered',
    partially_received: 'Part delivered',
    received: 'Delivered',
    cancelled: 'Cancelled',
  },
  expenseKind: {
    cogs: 'Reagent and consumables',
    payroll: 'Salaries and staff',
    overhead: 'Running costs',
    marketing: 'Marketing',
    equipment: 'Equipment and maintenance',
    other: 'Other',
  },
  whatsappShareStatus: {
    sending: 'Sending',
    sent: 'Sent',
    delivered: 'Delivered',
    read: 'Read',
    failed: 'Failed',
  },
  orderRequestStatus: {
    pending: 'Waiting',
    registered: 'Registered',
    declined: 'Set aside',
  },
} as const satisfies {
  gender: Record<Gender, string>;
  maritalStatus: Record<MaritalStatus, string>;
  paymentStatus: Record<PaymentStatus, string>;
  paymentMethod: Record<PaymentMethod, string>;
  sessionEndReason: Record<SessionEndReason, string>;
  reportStatus: Record<ReportStatus, string>;
  resultFlag: Record<ResultFlag, string>;
  reportKind: Record<ReportKind, string>;
  cultureOutcome: Record<CultureOutcome, string>;
  growthGrade: Record<GrowthGrade, string>;
  susceptibility: Record<Susceptibility, string>;
  stockUnit: Record<StockUnit, string>;
  stockMovementKind: Record<StockMovementKind, string>;
  lotStatus: Record<LotStatus, string>;
  importEntity: Record<ImportEntity, string>;
  importRunStatus: Record<ImportRunStatus, string>;
  importRowStatus: Record<ImportRowStatus, string>;
  importWriteMode: Record<ImportWriteMode, string>;
  requisitionStatus: Record<RequisitionStatus, string>;
  orderRequestStatus: Record<OrderRequestStatus, string>;
  whatsappShareStatus: Record<WhatsappShareStatus, string>;
  expenseKind: Record<ExpenseKind, string>;
};

/** Turns an ENUM_LABELS entry into `[{ value, label }]` for select inputs. */
export function toOptions<T extends string>(labels: Record<T, string>) {
  return (Object.entries(labels) as [T, string][]).map(([value, label]) => ({ value, label }));
}
